import { useLocation } from 'react-router-dom'
import { Sun, Moon, LogOut, User } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useLocalization } from '../contexts/LocalizationContext'
import LanguageSwitcher from './LanguageSwitcher'
import { useEffect, useState } from 'react'

export default function Header() {
  const { setTheme, isDark } = useTheme()
  const { signOut } = useAuth()
  const { t } = useLocalization()
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  // Esc closes the user menu (a11y).
  useEffect(() => {
    if (!userMenuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setUserMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [userMenuOpen])

  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/') return t('NAVIGATION.DASHBOARD')
    if (path === '/cards') return t('NAVIGATION.CREDIT_CARDS')
    if (path === '/cards/new') return t('CREDIT_CARDS.ADD_NEW')
    if (path.startsWith('/cards/') && path.includes('/edit')) return t('CREDIT_CARDS.EDIT_CARD')
    if (path === '/analytics') return t('NAVIGATION.ANALYTICS')
    if (path === '/users') return 'Users'
    if (path.startsWith('/users/')) return 'User detail'
    if (path === '/audit-log') return 'Audit log'
    if (path === '/offers') return 'Offer review'
    if (path === '/news') return 'News review'
    if (path === '/offer-sources') return 'Offer sources'
    if (path === '/mitc' || path.startsWith('/mitc/')) return 'MITC management'
    if (path === '/referrals') return 'Referral links'
    if (path === '/referral-analytics') return 'Referral analytics'
    if (path === '/cost-dashboard') return 'Cost dashboard'
    if (path === '/grievances') return 'Grievances'
    if (path === '/breach-notice') return 'Breach notices'
    return t('NAVIGATION.HOME')
  }

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <header className="h-16 flex items-center justify-between px-6 sm:px-8 bg-surface border-b border-line">
      <div className="flex items-center min-w-0">
        <h1 className="font-display text-xl sm:text-2xl text-ink truncate">
          {getPageTitle()}
        </h1>
      </div>

      <div className="flex items-center gap-1.5">
        <LanguageSwitcher />

        {/* Theme toggle */}
        <button
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="h-10 w-10 inline-flex items-center justify-center rounded-xl text-ink-muted hover:text-ink hover:bg-surface-elevated transition-colors"
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(v => !v)}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            className="h-10 w-10 inline-flex items-center justify-center rounded-xl hover:bg-surface-elevated transition-colors"
          >
            <span className="h-8 w-8 rounded-full bg-gradient-to-br from-brand to-brand-deep flex items-center justify-center">
              <User size={16} className="text-white" />
            </span>
          </button>

          {userMenuOpen && (
            <div role="menu" className="absolute right-0 mt-2 w-48 rounded-xl bg-surface-elevated border border-line shadow-lg py-1 z-50 animate-scale-in origin-top-right">
              <button
                onClick={handleLogout}
                role="menuitem"
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink hover:bg-surface transition-colors"
              >
                <LogOut size={16} className="text-ink-subtle" />
                {t('ACTIONS.SIGN_OUT')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </header>
  )
}
